import {
  ApolloQueryResult,
  OperationVariables,
  useLazyQuery,
  useMutation
} from '@apollo/client'
import {
  Button,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import PaymentIcon from '@material-ui/icons/Payment'
import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { ChangePriceModal } from '../../../../components/ChangePriceModal'
import { ENV } from '../../../../config/env'
import { CONFIRM_RESERVE_MUTATION } from '../../../../graphql/confirmReserve'
import { PREPARE_PAYMENT_MUTATION } from '../../../../graphql/confirmReservePrepare'
import { GET_GATE_WAY_LIST } from '../../../../graphql/gateways'
import { gatewayLogo } from '../../../../helpers/getGatewayLogo'
import { addCommaToPrice } from '../../../../helpers/numbers'
import { ErrorPreparePayment, extractError } from '../../../../helpers/regex'
import { cleanCartData } from '../../../../helpers/userData'
import { getTranslate, lang } from '../../../../localization'
import {
  ConfirmReserveInput,
  ConfirmReserveResponse,
  PreparePaymentInput,
  PreparePaymentResponse
} from '../../../../types/ConfirmReserve'
import { PMSStatus } from '../../../../types/booking'
import {
  CartPaymentInfo,
  PageStepper,
  PaymentMethod,
  ReservationTotalAmount
} from '../../../../types/cart'
import { GatewayListItem } from '../../../../types/gateway'
import { WalletListItem } from '../../../../types/wallet'
import BookingReceipt from '../BookingReceipt'
import { useStyles } from './styles/index.styles'

type PaymentGateWayProps = {
  totalAmount: ReservationTotalAmount | undefined
  paymentInfo: CartPaymentInfo | undefined
  confirmReserveInput: ConfirmReserveInput
  paymentMethod: PaymentMethod | undefined
  setPaymentMethod: React.Dispatch<
    React.SetStateAction<PaymentMethod | undefined>
  >
  isTimeExpired: boolean
  reservationNumber: string
  refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<
    ApolloQueryResult<{
      wallets: WalletListItem[]
    }>
  >
}

const PaymentGateWay: React.FC<PaymentGateWayProps> = ({
  totalAmount,
  paymentInfo,
  confirmReserveInput,
  setPaymentMethod,
  paymentMethod,
  isTimeExpired,
  reservationNumber,
  refetch
}: PaymentGateWayProps) => {
  const classes = useStyles()
  const history = useHistory()
  const params = useParams<{
    channelId: string
    code: string
    name: string
    page: PageStepper
    reservationNumber?: string
  }>()

  const [showBookingReceiptdModal, setShowBookingReceiptModal] = useState(false)
  const [disableAfterSuccess, setDisableAfterSuccess] = useState(false)
  const [ReceiptMode, setReceiptMode] =
    useState<
      Extract<
        PMSStatus,
        'Confirmed' | 'Cancelled' | 'ModificationReserved' | 'ReservationFailed'
      >
    >('Confirmed')

  const [selectedGatewayId, setSelectedGatewayId] = useState<string | null>(
    null
  )

  const [recieptModalData, setRecieptModalData] =
    useState<ConfirmReserveResponse>({
      reservationNumber: '',
      displayReservationNumber: '',
      description: '',
      status: {
        PMS: 'Cancelled',
        CRS: 'Cancelled'
      },
      wallet: '',
      paymentDate: '',
      providerName: '',
      additionalDiscount: 0,
      totalDiscount: 0,
      subtotalBeforeDiscount: 0,
      subtotalAfterDiscount: 0,
      totalPayable: 0,
      roomStaysReserveDetails: []
    })

  const [preparePaymentInput, setPreparePaymentInput] =
    useState<PreparePaymentInput>({
      paymentType: 'Booking',
      gatewayNumber: 0,
      paymentCode: reservationNumber,
      amount: 0,
      failRoute: `${window.location.origin}/${lang}/booking/list/${reservationNumber}/view`,
      successRoute: `${window.location.origin}/${lang}/booking/list/${reservationNumber}/view`,
      sourceType: 'TA'
    })
  const [showPriceModal, setShowPriceModal] = useState(false)

  //GETWAY_LIST_QUERY
  const [
    getGateWayList,
    { data: gateWayList, loading: loadingForGatewayList }
  ] = useLazyQuery<{
    gatewayList: GatewayListItem[]
  }>(GET_GATE_WAY_LIST, {
    notifyOnNetworkStatusChange: true
  })

  useEffect(() => {
    if (reservationNumber && paymentInfo && paymentInfo.walletType) {
      getGateWayList({
        variables: {
          input: {
            reservationNumber: reservationNumber,
            paymentType: 'Booking',
            walletType: paymentInfo?.walletType
          }
        }
      })
    }
  }, [reservationNumber, paymentInfo])

  useEffect(() => {
    const reservNo = reservationNumber || params.reservationNumber || ''
    setPreparePaymentInput({
      ...preparePaymentInput,
      paymentCode: reservNo,
      failRoute: `${window.location.origin}/${lang}/booking/list/${reservNo}/view`,
      successRoute: `${window.location.origin}/${lang}/booking/list/${reservNo}/view`
    })
  }, [reservationNumber, params.reservationNumber])

  //CONFIRM_RESERVE_MUTATION
  const [confirmReserve, { loading: loadingForReserve }] = useMutation<{
    confirmReserve: ConfirmReserveResponse
  }>(CONFIRM_RESERVE_MUTATION, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (res) => {
      cleanCartData()
      localStorage.removeItem('confirmPrimitiveInput')
      localStorage.removeItem('confirmPrimitiveOutput')
      localStorage.removeItem('hotelDetails')
      localStorage.removeItem('primitiveData')
      setRecieptModalData(res.confirmReserve)
      setReceiptMode('Confirmed')
      setShowBookingReceiptModal(true)
      setDisableAfterSuccess(true)
    },
    onError: (err) => {
      setReceiptMode('ReservationFailed')
      setShowBookingReceiptModal(true)
      toast.error(err.message)
    }
  })

  const [preparePayment, { loading: loadingForPrepare }] = useMutation<{
    preparePayment: PreparePaymentResponse
  }>(PREPARE_PAYMENT_MUTATION, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (res) => {
      const url = `${ENV.BASE_URL}/v1/payment/redirect?PaymentNumber=${res.preparePayment.paymentNumber}`
      window.location.href = url
    },
    onError: (err) => {
      toast.error(err.message)
      // graphQLErrors is empty on a network failure and absent when the value reaching here
      // is not an ApolloError, so indexing [0] crashes the error handler itself.
      const firstGraphQLError = err?.graphQLErrors?.[0]
      if (
        paymentMethod === 'WalletBalance' &&
        firstGraphQLError &&
        extractError(firstGraphQLError.extensions) === ErrorPreparePayment
      ) {
        refetch()
        setShowPriceModal(true)
      }
    }
  })

  const confirmButtonLoading = loadingForReserve || loadingForPrepare

  const handleClickOnConfirm = () => {
    const shouldPreparePayment =
      paymentMethod === 'Online' ||
      (paymentMethod !== 'CreditBalance' &&
        totalAmount?.payable! - paymentInfo?.cashBalance! > 0)

    if (shouldPreparePayment) {
      preparePayment({
        variables: {
          input: {
            ...preparePaymentInput,
            amount:
              paymentMethod === 'Online'
                ? 0
                : paymentInfo?.cashBalance! > 0
                ? totalAmount?.payable! - paymentInfo?.cashBalance!
                : totalAmount?.payable!
          }
        }
      })
    } else {
      confirmReserve({
        variables: {
          input: {
            ...confirmReserveInput,
            reservationNumber
          }
        }
      })
    }
  }

  const noGatewayOption = gateWayList && gateWayList.gatewayList.length < 1

  return (
    <>
      <div className={classes.container}>
        <div className={classes.header}>
          <Typography className={classes.title}>
            {getTranslate('Payment Gateway')}
          </Typography>
        </div>
        <Divider className={classes.divider} />
        <div className={classes.content}>
          <RadioGroup className={classes.radioGroup}>
            {[
              {
                value: 'Online',
                label: 'Online',
                // A zero-payable reservation has nothing for a gateway to charge --
                // WalletBalance/CreditBalance both route straight to confirmReserve
                // (skipping preparePayment) for that case; Online always goes through
                // the gateway and would just bounce off the backend's own amount<=0
                // rejection, so it's excluded here rather than left to fail later.
                disable: false || noGatewayOption || totalAmount?.payable === 0
              },
              {
                value: 'WalletBalance',
                label: 'Cash Balance',
                // Paying from the wallet's own balance does not go through a payment
                // gateway, so an empty gateway list must not disable it. It did, which left
                // the balance displayed beside an option that could not be chosen -- on
                // staging, 494,000,000 IRR shown and unusable. Credit Balance was never
                // gated this way, which is the intent this restores.
                disable: false
              },
              {
                value: 'CreditBalance',
                label: 'Credit Balance',
                disable: false
              }
            ].map((option, i) => (
              <div
                key={i}
                className={[
                  classes.options,
                  paymentMethod === option.value ? classes.selectedOption : ''
                ].join(' ')}>
                <div className={classes.optionRow}>
                  <FormControlLabel
                    key={i}
                    control={
                      <Radio
                        disabled={option.disable}
                        color="primary"
                        size="small"
                        className={classes.radioRoot}
                      />
                    }
                    label={getTranslate(option.label)}
                    className={classes.formControl}
                    onChange={() =>
                      setPaymentMethod(option.value as PaymentMethod)
                    }
                    checked={paymentMethod === option.value}
                    disabled={
                      option.disable ||
                      (option.value === 'CreditBalance' &&
                        paymentInfo?.remainingCreditBalance! <
                          totalAmount?.payable!)
                    }
                  />
                  {!paymentMethod && (
                    <Typography variant="h6" className={classes.text}>
                      {option.value === 'WalletBalance'
                        ? `${addCommaToPrice(
                            paymentInfo?.cashBalance! > 0
                              ? paymentInfo?.cashBalance!
                              : 0
                          )} ${getTranslate(paymentInfo?.currency || '')}`
                        : option.value === 'CreditBalance'
                        ? `${addCommaToPrice(
                            paymentInfo?.remainingCreditBalance!
                          )} ${getTranslate(paymentInfo?.currency || '')}`
                        : ''}
                    </Typography>
                  )}
                </div>
                <Collapse
                  in={paymentMethod === option.value}
                  timeout="auto"
                  unmountOnExit>
                  {option.value === 'Online' && !loadingForGatewayList && (
                    <div className={classes.gatewayContainer}>
                      {gateWayList?.gatewayList.map((gateway) => {
                        const isSelected = selectedGatewayId === gateway.ID
                        return (
                          <div
                            key={gateway.ID}
                            className={`${classes.bank} ${
                              isSelected ? classes.bankSelected : ''
                            }`}
                            onClick={() => {
                              setSelectedGatewayId(
                                selectedGatewayId ? '' : gateway.ID
                              )
                              setPreparePaymentInput({
                                ...preparePaymentInput,
                                gatewayNumber: gateway.gateWayNumber
                              })
                            }}>
                            <img
                              src={gatewayLogo(gateway.gateWayType)}
                              alt={gateway.gateWayType}
                              className={classes.bankIcon}
                            />
                          </div>
                        )
                      })}
                    </div>
                  )}
                  {option.value === 'WalletBalance' && (
                    <>
                      <div className={classes.balanceRow}>
                        <Typography variant="h6" className={classes.text}>
                          {getTranslate('Cash Balance')}
                        </Typography>
                        <Typography variant="h6" className={classes.text}>
                          {`${addCommaToPrice(
                            paymentInfo?.cashBalance! > 0
                              ? paymentInfo?.cashBalance!
                              : 0
                          )} ${getTranslate(paymentInfo?.currency || '')}`}
                        </Typography>
                      </div>
                      {paymentInfo?.cashBalance! < totalAmount?.payable! &&
                        !loadingForGatewayList && (
                          <>
                            <div className={classes.balanceRow}>
                              <Typography variant="h6" className={classes.text}>
                                {getTranslate('Total Amount')}
                              </Typography>
                              <Typography variant="h6" className={classes.text}>
                                {`${addCommaToPrice(
                                  totalAmount?.payable!
                                )} ${getTranslate(
                                  paymentInfo?.currency || ''
                                )}`}
                              </Typography>
                            </div>
                            <Divider className={classes.dashedDivider} />
                            <div className={classes.balanceRow}>
                              <Typography variant="h6" className={classes.text}>
                                {getTranslate('Reamining Amount')}
                              </Typography>
                              <Typography variant="h6" className={classes.text}>
                                {`${addCommaToPrice(
                                  paymentInfo?.cashBalance! > 0
                                    ? totalAmount?.payable! -
                                        paymentInfo?.cashBalance!
                                    : totalAmount?.payable!
                                )} ${getTranslate(
                                  paymentInfo?.currency || ''
                                )}`}
                              </Typography>
                            </div>
                            <div className={classes.iconTextRow}>
                              <InfoOutlinedIcon className={classes.infoIcon} />
                              <Typography
                                variant="h6"
                                className={classes.greyText}>
                                {getTranslate(
                                  'You have to pay remaining amount with online payment'
                                )}
                              </Typography>
                            </div>
                            <Typography
                              variant="h6"
                              className={classes.boldtext}>
                              {getTranslate(
                                'Choose online gateway for remaining amount'
                              )}
                            </Typography>
                            <div className={classes.gatewayContainer}>
                              {gateWayList?.gatewayList.map((gateway) => {
                                const isSelected =
                                  selectedGatewayId === gateway.ID
                                return (
                                  <div
                                    key={gateway.ID}
                                    className={`${classes.bank} ${
                                      isSelected ? classes.bankSelected : ''
                                    }`}
                                    onClick={() => {
                                      setSelectedGatewayId(
                                        selectedGatewayId ? '' : gateway.ID
                                      )
                                      setPreparePaymentInput({
                                        ...preparePaymentInput,
                                        gatewayNumber: gateway.gateWayNumber
                                      })
                                    }}>
                                    <img
                                      src={gatewayLogo(gateway.gateWayType)}
                                      alt={gateway.gateWayType}
                                      className={classes.bankIcon}
                                    />
                                  </div>
                                )
                              })}
                            </div>
                          </>
                        )}
                    </>
                  )}
                  {option.value === 'CreditBalance' && (
                    <>
                      <div className={classes.balanceRow}>
                        <Typography variant="h6" className={classes.text}>
                          {getTranslate('Credit Balance')}
                        </Typography>
                        <Typography variant="h6" className={classes.text}>
                          {`${addCommaToPrice(
                            paymentInfo?.remainingCreditBalance!
                          )} ${getTranslate(paymentInfo?.currency || '')}`}
                        </Typography>
                      </div>
                      {paymentInfo?.remainingCreditBalance! <
                        totalAmount?.payable! && (
                        <div className={classes.iconTextRow}>
                          <InfoOutlinedIcon className={classes.infoIcon} />
                          <Typography variant="h6" className={classes.greyText}>
                            {getTranslate(
                              'Incifficient Ballance! You can not use this gateway'
                            )}
                          </Typography>
                        </div>
                      )}
                    </>
                  )}
                </Collapse>
              </div>
            ))}
          </RadioGroup>
        </div>
        <div className={classes.buttons}>
          <Button
            className={[
              classes.confirmBtn,
              !!paymentMethod ? classes.selectedConfirmBtn : '',
              confirmButtonLoading ||
              (paymentMethod === 'WalletBalance' &&
              paymentInfo?.cashBalance! >= totalAmount?.payable!
                ? false
                : !selectedGatewayId && paymentMethod !== 'CreditBalance')
                ? classes.disabled
                : ''
            ].join(' ')}
            disabled={
              !paymentMethod ||
              isTimeExpired ||
              // A payable of exactly 0 is a legitimate, confirmable state (a fully
              // discounted reservation) -- WalletBalance/CreditBalance both route to
              // confirmReserve directly for it via handleClickOnConfirm, with no
              // gateway involved. Only a negative payable (which should never happen,
              // but this screen otherwise has no explicit guard against it) stays
              // blocked here.
              (totalAmount?.payable ?? 0) < 0 ||
              disableAfterSuccess ||
              confirmButtonLoading ||
              (paymentMethod === 'WalletBalance' &&
              paymentInfo?.cashBalance! >= totalAmount?.payable!
                ? false
                : !selectedGatewayId && paymentMethod !== 'CreditBalance')
            }
            startIcon={
              !!paymentMethod ? (
                <PaymentIcon className={classes.peymentIcon} />
              ) : undefined
            }
            onClick={() => {
              handleClickOnConfirm()
            }}>
            {!!paymentMethod ? (
              <>
                {paymentMethod === 'Online' && (
                  <>
                    <Typography variant="h4" className={classes.whiteText}>
                      {`${getTranslate('Pay')} | ${addCommaToPrice(
                        totalAmount?.payable!
                      )} ${getTranslate(paymentInfo?.currency || '')}
                    `}
                    </Typography>
                    {confirmButtonLoading && (
                      <CircularProgress
                        className={classes.circularProgress}
                        size={16}
                      />
                    )}
                  </>
                )}
                {paymentMethod === 'WalletBalance' &&
                  (paymentInfo?.cashBalance! > 0 &&
                  paymentInfo?.cashBalance! < totalAmount?.payable! ? (
                    <>
                      <Typography variant="h4" className={classes.whiteText}>
                        {`${getTranslate('Pay')} | ${addCommaToPrice(
                          totalAmount?.payable! - paymentInfo?.cashBalance!
                        )} ${getTranslate(paymentInfo?.currency || '')}`}
                      </Typography>
                      {confirmButtonLoading && (
                        <CircularProgress
                          className={classes.circularProgress}
                          size={16}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <Typography variant="h4" className={classes.whiteText}>
                        {`${getTranslate('Pay')} | ${addCommaToPrice(
                          totalAmount?.payable!
                        )} ${getTranslate(paymentInfo?.currency || '')}`}
                      </Typography>
                      {confirmButtonLoading && (
                        <CircularProgress
                          className={classes.circularProgress}
                          size={16}
                        />
                      )}
                    </>
                  ))}
                {paymentMethod === 'CreditBalance' &&
                  (paymentInfo?.remainingCreditBalance! <
                  totalAmount?.payable! ? (
                    <>
                      <Typography variant="h4" className={classes.whiteText}>
                        {`${getTranslate('Pay')}| ${addCommaToPrice(
                          totalAmount?.payable! -
                            paymentInfo?.remainingCreditBalance!
                        )} ${getTranslate(paymentInfo?.currency || '')}`}
                      </Typography>
                      {confirmButtonLoading && (
                        <CircularProgress
                          className={classes.circularProgress}
                          size={16}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <Typography variant="h4" className={classes.whiteText}>
                        {`${getTranslate('Pay')} | ${addCommaToPrice(
                          totalAmount?.payable!
                        )} ${getTranslate(paymentInfo?.currency || '')}`}
                      </Typography>
                      {confirmButtonLoading && (
                        <CircularProgress
                          className={classes.circularProgress}
                          size={16}
                        />
                      )}
                    </>
                  ))}
              </>
            ) : (
              <Typography variant="h4" className={classes.whiteText}>
                {getTranslate('Confirm & Continue')}
              </Typography>
            )}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              if (params.reservationNumber) {
                history.push(
                  `/${lang}/booking/list/${params.reservationNumber}/edit`
                )
              } else {
                history.push(
                  `/${lang}/book/${params.channelId}/${params.code}/${params.name}`
                )
              }

              cleanCartData()
              localStorage.removeItem('confirmPrimitiveInput')
              localStorage.removeItem('confirmPrimitiveOutput')
              localStorage.removeItem('hotelDetails')
              localStorage.removeItem('primitiveData')
            }}>
            <Typography variant="h4" className={classes.blackText}>
              {getTranslate('Cancel ')}
            </Typography>
          </Button>
        </div>
      </div>
      <BookingReceipt
        mode={ReceiptMode}
        open={showBookingReceiptdModal}
        onClose={() => {}}
        data={recieptModalData}
        reservationNumber={String(recieptModalData.reservationNumber)}
      />
      <ChangePriceModal
        title={'Price Change Warning'}
        subTitle={
          'Your balance has changed, and the online charge amount is now lower than the amount required to complete the reservation. If you wish to proceed with the reservation using the new amount, please click the button below.'
        }
        isOpen={showPriceModal}
        onClose={() => {
          setShowPriceModal(false)
        }}
        price={
          paymentInfo?.cashBalance! > 0
            ? totalAmount?.payable! - paymentInfo?.cashBalance!
            : totalAmount?.payable!
        }
        currency={paymentInfo?.currency}
        onClick={() => {
          preparePayment({
            variables: {
              input: {
                ...preparePaymentInput,
                amount:
                  paymentInfo?.cashBalance! > 0
                    ? totalAmount?.payable! - paymentInfo?.cashBalance!
                    : totalAmount?.payable!
              }
            }
          })
        }}
      />
    </>
  )
}

export default PaymentGateWay
